import { directoryLabel } from '../utils/visibility.utils';

export const UBERALL_ERROR_CODES = [
  'UBERALL_RATE_LIMITED',
  'UBERALL_TEMPORARILY_UNAVAILABLE',
  'UBERALL_NOT_AUTHORISED',
  'UBERALL_CONFLICT',
  'UBERALL_REQUEST_REJECTED',
  'UBERALL_NOT_FOUND',
  'UBERALL_REVIEW_GONE',
  'UBERALL_TIMEOUT',
  'UBERALL_MANUAL_VALIDATION_REQUIRED',
  'UBERALL_AUTO_RESPONSE_CONFLICT',
  'UBERALL_AUTO_RESPONSE_RULE_NOT_FOUND',
  'UBERALL_REPLY_TEMPLATE_LIMIT_REACHED',
  'UBERALL_INVALID_PERIOD',
  'UBERALL_TOO_MANY_KEYWORDS',
  'UBERALL_BLANK_KEYWORDS',
  'UBERALL_AI_EMPTY_RESPONSE',
  'SEO_REPORT_NOT_GENERATED',
  'SEO_REPORT_NO_RECIPIENT',
  'SEO_REPORT_EMAIL_FAILED',
  'SEO_REPORT_STATUS_UNAVAILABLE',
  'UBERALL_CONNECT_NOT_AVAILABLE',
  'UBERALL_DISCONNECT_NOT_AVAILABLE',
  'UBERALL_DIRECTORY_NOT_CONNECTED',
  'UBERALL_SOCIAL_POST_INVALID',
  'UBERALL_STORE_DEACTIVATED',
] as const;

export type UberallErrorCode = (typeof UBERALL_ERROR_CODES)[number];

interface BackendErrorBody {
  code?: string | number;
  message?: string;
  status?: string;
  details?: unknown[];
}

interface AxiosLikeError {
  response?: { status?: number; data?: BackendErrorBody & { error?: BackendErrorBody | string } };
}

// services-v2 nests its envelope under `error`; the router's own 502/504 puts a plain string there.
const bodyOf = (error: unknown): BackendErrorBody | undefined => {
  const data = (error as AxiosLikeError | undefined)?.response?.data;
  return typeof data?.error === 'object' ? data.error : data;
};

const isKnown = (status: string | undefined): status is UberallErrorCode =>
  Boolean(status) && (UBERALL_ERROR_CODES as readonly string[]).includes(status as string);

export const uberallErrorCode = (error: unknown): UberallErrorCode | null => {
  const status = bodyOf(error)?.status;
  return isKnown(status) ? status : null;
};

export const uberallErrorKey = (error: unknown): string => {
  const code = uberallErrorCode(error);
  return code ? `visibility.errors.uberall.${code}` : 'visibility.errors.generic';
};

interface DirectoryDetail {
  directory: string;
  reason: string | null;
}

/** The directories a social posting refusal names: one `{ directory, reason? }` per directory at fault. */
export const uberallErrorDetails = (error: unknown): DirectoryDetail[] =>
  (bodyOf(error)?.details ?? []).flatMap(detail => {
    if (typeof detail === 'string') return detail ? [{ directory: detail, reason: null }] : [];
    const { directory, reason } = (detail ?? {}) as { directory?: unknown; reason?: unknown };
    if (typeof directory !== 'string' || !directory) return [];
    return [{ directory, reason: typeof reason === 'string' ? reason : null }];
  });

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** The toast for a failed call: a social refusal says, per directory, what it refused. */
export const uberallErrorMessage = (error: unknown, translate: Translate): string => {
  const details = uberallErrorDetails(error);
  const labelOf = (directory: string) => directoryLabel(translate, directory);

  if (uberallErrorCode(error) === 'UBERALL_SOCIAL_POST_INVALID' && details.length > 0) {
    return details
      .map(({ directory, reason }) =>
        translate(`visibility.errors.socialPostInvalid.${reason ?? 'UNKNOWN'}`, {
          directory: labelOf(directory),
          defaultValue: translate('visibility.errors.socialPostInvalid.UNKNOWN', { directory: labelOf(directory) }),
        }),
      )
      .join(' ');
  }

  return translate(uberallErrorKey(error), {
    directory: details.length
      ? details.map(({ directory }) => labelOf(directory)).join(', ')
      : translate('visibility.errors.thisDirectory'),
  });
};
